// BuilderSceneManager - マップエディター用のシーン管理
// グリッド管理、アセット配置時の地面レイヤー操作を提供

import "@babylonjs/core/Rendering/boundingBoxRenderer";
import { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { BoundingBox } from "@babylonjs/core/Culling/boundingBox";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Vector3D } from "common/types";
import { AssetModelType } from "common/model";
import { EventBus } from "features/builder/eventBus";
import { MAP_MESH_SIZE, DEFAULT_MAP_SIZE, MATERIAL_ALPHA } from "features/builder/constants";

export interface GridCell {
  x: number;
  z: number;
  layer: number;
  mesh: Mesh;
}

export interface BuilderSceneConfig {
  mapSize?: Vector3D;
}

export class BuilderSceneManager {
  private eventBus: EventBus;
  private scene: Scene | null = null;
  private groundMeshes: Map<string, Mesh> = new Map();
  private groundMaterial: StandardMaterial | null = null;
  private groundLayerMaterial: StandardMaterial | null = null;
  private mapSize: Vector3D;

  constructor(eventBus: EventBus, config?: BuilderSceneConfig) {
    this.eventBus = eventBus;
    this.mapSize = config?.mapSize ?? DEFAULT_MAP_SIZE;
  }

  // 既存シーンでグリッドを初期化
  initializeWithScene(scene: Scene): void {
    this.scene = scene;
    this.initializeGrid(scene);
  }

  // グリッドを初期化
  private initializeGrid(scene: Scene): void {
    const width = this.mapSize.x;
    const height = this.mapSize.z;

    // グラウンドマテリアル（レイヤー0用）
    this.groundMaterial = new StandardMaterial("ground", scene);
    this.groundMaterial.alpha = MATERIAL_ALPHA.ground;
    this.groundMaterial.diffuseColor = new Color3(0, 1, 0);

    // グラウンドレイヤーマテリアル（レイヤー1以上用）
    this.groundLayerMaterial = new StandardMaterial("ground_layer", scene);
    this.groundLayerMaterial.alpha = MATERIAL_ALPHA.ground;
    this.groundLayerMaterial.diffuseColor = new Color3(0, 1, 0);

    // グリッドメッシュを作成
    for (let z = 0; z < height; z++) {
      for (let x = 0; x < width; x++) {
        const gridX = x * MAP_MESH_SIZE + 1;
        const gridZ = z * MAP_MESH_SIZE + 1;
        const name = `asset_ground_layer_${gridX}_${gridZ}_0`;

        const ground = MeshBuilder.CreateGround(
          name,
          { width: MAP_MESH_SIZE, height: MAP_MESH_SIZE },
          scene,
        );
        ground.id = "ground";
        ground.material = this.groundMaterial;
        ground.showBoundingBox = true;
        ground.position = new Vector3(
          x * MAP_MESH_SIZE + MAP_MESH_SIZE / 2,
          0,
          z * MAP_MESH_SIZE + MAP_MESH_SIZE / 2,
        );

        this.groundMeshes.set(name, ground);
      }
    }
  }

  // グリッドプレビュー表示を切り替え
  setGridPreviewVisible(visible: boolean): void {
    const alpha = visible ? MATERIAL_ALPHA.groundPreview : MATERIAL_ALPHA.ground;
    if (this.groundLayerMaterial) {
      this.groundLayerMaterial.alpha = alpha;
    }
  }

  // ベースグリッド表示を切り替え（RIVER/GROUND用）
  setBaseGridPreviewVisible(visible: boolean): void {
    const alpha = visible ? MATERIAL_ALPHA.groundPreview : MATERIAL_ALPHA.ground;
    if (this.groundMaterial) {
      this.groundMaterial.alpha = alpha;
    }
  }

  // グリッドを非表示
  hideAllGridPreview(): void {
    if (this.groundMaterial) {
      this.groundMaterial.alpha = MATERIAL_ALPHA.ground;
    }
    if (this.groundLayerMaterial) {
      this.groundLayerMaterial.alpha = MATERIAL_ALPHA.ground;
    }
  }

  // アセット配置時の地面レイヤー作成
  // GROUNDタイプの場合は上層に地面を作成、それ以外は下層の地面を削除
  createAssetGround(
    boundingBox: BoundingBox,
    type: AssetModelType,
    position: Vector3D,
    bottomLayer: number,
  ): void {
    if (!this.scene) return;

    const { width, height } = this.calcMeshSize(boundingBox);
    const originX = Math.floor(position.x + MAP_MESH_SIZE / 2 - width / 2);
    const originY = Math.floor(position.z + MAP_MESH_SIZE / 2 - height / 2);

    for (let x = 0; x < Math.floor(width > 2 ? width / 2 : MAP_MESH_SIZE / 2); x++) {
      for (let y = 0; y < Math.floor(height > 2 ? height / 2 : MAP_MESH_SIZE / 2); y++) {
        if (type === AssetModelType.GROUND) {
          // GROUNDタイプ: 上層に地面を作成
          const groundName = `asset_ground_layer_${originX + x * MAP_MESH_SIZE}_${
            originY + y * MAP_MESH_SIZE
          }_${Math.round(boundingBox.maximumWorld.y)}`;

          const ground = MeshBuilder.CreateGround(
            groundName,
            { width: MAP_MESH_SIZE, height: MAP_MESH_SIZE },
            this.scene,
          );
          ground.id = "ground";
          ground.material = this.groundLayerMaterial;
          ground.position = new Vector3(
            originX + x * MAP_MESH_SIZE,
            boundingBox.maximumWorld.y,
            originY + y * MAP_MESH_SIZE,
          );
          ground.showBoundingBox = true;
          this.groundMeshes.set(groundName, ground);
        }

        // 下層の地面を削除
        const bottomGroundName = `asset_ground_layer_${originX + x * MAP_MESH_SIZE}_${
          originY + y * MAP_MESH_SIZE
        }_${bottomLayer}`;
        const bottomGround = this.groundMeshes.get(bottomGroundName);
        if (bottomGround) {
          bottomGround.dispose();
          this.groundMeshes.delete(bottomGroundName);
        }
      }
    }
  }

  // アセット移動/削除時の地面補填
  // 元の位置に地面を作成
  fillBottomGround(boundingBox: BoundingBox, position: Vector3D): void {
    if (!this.scene) return;

    const { width, height } = this.calcMeshSize(boundingBox);
    const originX = Math.floor(position.x + MAP_MESH_SIZE / 2 - width / 2);
    const originY = Math.floor(position.z + MAP_MESH_SIZE / 2 - height / 2);

    for (let x = 0; x < Math.floor(width > 2 ? width / 2 : MAP_MESH_SIZE / 2); x++) {
      for (let y = 0; y < Math.floor(height > 2 ? height / 2 : MAP_MESH_SIZE / 2); y++) {
        // 上層の地面を削除
        const topGroundName = `asset_ground_layer_${originX + x * MAP_MESH_SIZE}_${
          originY + y * MAP_MESH_SIZE
        }_${Math.round(boundingBox.maximumWorld.y)}`;
        const topGround = this.groundMeshes.get(topGroundName);
        if (topGround) {
          topGround.dispose();
          this.groundMeshes.delete(topGroundName);
        }

        // 下層に地面を作成
        const bottomGroundName = `asset_ground_layer_${originX + x * MAP_MESH_SIZE}_${
          originY + y * MAP_MESH_SIZE
        }_${Math.round(position.y)}`;

        // 既存の地面があれば作成しない
        if (this.groundMeshes.has(bottomGroundName)) continue;

        const bottomGround = MeshBuilder.CreateGround(
          bottomGroundName,
          { width: MAP_MESH_SIZE, height: MAP_MESH_SIZE },
          this.scene,
        );
        bottomGround.id = "ground";
        bottomGround.showBoundingBox = true;
        bottomGround.material =
          Math.round(position.y) === 0 ? this.groundMaterial : this.groundLayerMaterial;
        bottomGround.position = new Vector3(
          originX + x * MAP_MESH_SIZE,
          position.y,
          originY + y * MAP_MESH_SIZE,
        );
        this.groundMeshes.set(bottomGroundName, bottomGround);
      }
    }
  }

  // メッシュサイズを計算
  private calcMeshSize(boundingBox: BoundingBox): { width: number; height: number } {
    return {
      width: Math.round(boundingBox.maximum.x - boundingBox.minimum.x),
      height: Math.round(boundingBox.maximum.z - boundingBox.minimum.z),
    };
  }

  // リソース解放
  dispose(): void {
    this.groundMeshes.forEach((mesh) => mesh.dispose());
    this.groundMeshes.clear();
    this.groundMaterial?.dispose();
    this.groundLayerMaterial?.dispose();
  }
}
